<template>
  <v-dialog v-model="internalValue" max-width="600px" persistent>
    <v-card class="rounded-xl overflow-hidden elevation-6">
      <!-- HEADER -->
      <v-card-title class="pa-4 bg-gray-50 border-bottom d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-avatar color="orange lighten-5" size="38" class="mr-3">
            <v-icon color="orange darken-3">mdi-presentation</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
              Material y Presentación de la Sesión
            </div>
            <div class="text-caption grey--text text--darken-1">
              Sesión #{{ session.TseSessionNumber }} — {{ session.TseTopicTitle || 'General' }}
            </div>
          </div>
        </div>
        <v-btn icon small @click="internalValue = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-5">
        <!-- DETALLES DE LA SESIÓN -->
        <v-card class="pa-3 mb-4 rounded-lg bg-gray-50" outlined elevation="0">
          <v-row dense class="text-caption">
            <v-col cols="12" sm="6">
              <span class="grey--text text--darken-2 font-weight-bold">Capacitador:</span>
              <span class="ml-1 font-weight-medium">{{ session.TrnFullName || 'No asignado' }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="grey--text text--darken-2 font-weight-bold">Fecha:</span>
              <span class="ml-1 font-weight-medium">{{ formatDate(session.TseDate) }} ({{ session.TseStartTime }} - {{ session.TseEndTime }})</span>
            </v-col>
          </v-row>
        </v-card>

        <!-- OPCIÓN A: ENLACE WEB DE LA PRESENTACIÓN (OneDrive / SharePoint / Canva / Drive) -->
        <div class="mb-4">
          <div class="text-caption font-weight-bold grey--text text--darken-3 mb-1 text-uppercase">
            1. Enlace Web de la Presentación / Diapositivas
          </div>
          <v-text-field
            v-model="materialLink"
            outlined
            dense
            hide-details
            placeholder="https://sunshine.sharepoint.com/... o enlace de Canva / Google Drive"
            prepend-inner-icon="mdi-link-variant"
            class="rounded-lg"
          >
            <template v-slot:append v-if="materialLink">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small color="primary" v-bind="attrs" v-on="on" :href="materialLink" target="_blank">
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <span>Probar y abrir enlace</span>
              </v-tooltip>
            </template>
          </v-text-field>
          <div class="text-caption grey--text text--darken-1 mt-1">
            Pegue el enlace compartido para que los colaboradores puedan revisar el material en línea o desde la App móvil.
          </div>
        </div>

        <v-divider class="my-4"></v-divider>

        <!-- OPCIÓN B: SUBIDA DE ARCHIVO (PPTX / PPT / PDF) -->
        <div>
          <div class="text-caption font-weight-bold grey--text text--darken-3 mb-1 text-uppercase">
            2. Archivo de Diapositivas / Presentación (PPT, PPTX o PDF)
          </div>

          <!-- Archivo existente -->
          <div v-if="session.TseMaterialPath" class="d-flex align-center justify-space-between pa-3 rounded-lg border-light mb-3 bg-white">
            <div class="d-flex align-center text-truncate mr-2">
              <v-icon color="orange darken-2" class="mr-2">mdi-file-powerpoint</v-icon>
              <div>
                <div class="text-body-2 font-weight-bold text-truncate" :title="getFileName(session.TseMaterialPath)">
                  {{ getFileName(session.TseMaterialPath) }}
                </div>
                <div class="text-caption grey--text text--darken-1">
                  Archivo cargado en el servidor
                </div>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-btn
                small
                outlined
                color="orange darken-2"
                class="rounded-lg text-none font-weight-bold mr-1"
                :loading="downloading"
                @click="downloadMaterial"
              >
                <v-icon left small>mdi-download</v-icon>
                Descargar
              </v-btn>
              <v-btn icon small color="error" @click="removeMaterialFile" title="Quitar archivo de presentación">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Selector de nuevo archivo -->
          <v-file-input
            v-model="selectedFile"
            label="Seleccionar o arrastrar archivo PPTX / PDF"
            outlined
            dense
            accept=".ppt,.pptx,.pdf,.pps,.ppsx"
            prepend-icon=""
            prepend-inner-icon="mdi-cloud-upload-outline"
            class="rounded-lg"
            hide-details
            placeholder="Seleccione presentación (.pptx, .ppt o .pdf)"
          ></v-file-input>
          <div class="text-caption grey--text text--darken-1 mt-1">
            Formatos soportados: Microsoft PowerPoint (.pptx, .ppt) o Adobe PDF (.pdf) hasta 50MB.
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- BOTONES DE ACCIÓN -->
      <v-card-actions class="pa-4 bg-gray-50 d-flex justify-end">
        <v-btn text class="font-weight-bold px-4 text-none rounded-lg" @click="internalValue = false">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          class="font-weight-bold px-4 text-none rounded-lg"
          elevation="0"
          :loading="saving"
          @click="saveMaterial"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Guardar Material
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _GenUploadFiles from "@/services/General/GenUploadFiles";
import TrainingSessionService from "@/services/Management/TrainingSessionService";

export default {
  name: "TrainingSessionMaterialModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    session: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      materialLink: "",
      selectedFile: null,
      saving: false,
      downloading: false,
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        if (!val) {
          this.selectedFile = null;
        }
      },
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.materialLink = this.session?.TseMaterialLink || "";
        this.selectedFile = null;
      }
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const parts = String(dateStr).split("T")[0].split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return dateStr;
    },
    getFileName(path) {
      if (!path) return "";
      return path.split("/").pop();
    },
    async downloadMaterial() {
      if (!this.session?.TseMaterialPath) return;
      this.downloading = true;
      try {
        await _GenUploadFiles.getFile("PathTrainingMaterials", this.session.TseMaterialPath);
      } catch (err) {
        this.$fun.alert("Error al descargar el archivo de presentación.", "error");
      } finally {
        this.downloading = false;
      }
    },
    removeMaterialFile() {
      this.$fun.alert("¿Está seguro de quitar el archivo de presentación de esta sesión?", "question").then((r) => {
        if (r && r.value) {
          this.session.TseMaterialPath = null;
          this.saveMaterial();
        }
      });
    },
    async saveMaterial() {
      this.saving = true;
      try {
        let uploadedFileName = this.session?.TseMaterialPath || null;

        // Subir nuevo archivo si fue seleccionado
        if (this.selectedFile && typeof this.selectedFile === "object" && this.selectedFile instanceof File) {
          const sessionId = this.session?.TseID || "SES";
          const uniqueName = `Material_Ses_${sessionId}_${Date.now()}`;
          const res = await _GenUploadFiles.upload(
            this.selectedFile,
            uniqueName,
            "PathTrainingMaterials",
            true
          );
          uploadedFileName = res?.data?.fileName || `${uniqueName}.${this.selectedFile.name.split('.').pop()}`;
        }

        const reqId = this.$fun.getUserID();
        const payload = Object.assign({}, this.session, {
          TseMaterialLink: this.materialLink ? this.materialLink.trim() : null,
          TseMaterialPath: uploadedFileName,
        });
        if (payload.TseDate && String(payload.TseDate).includes("T")) {
          payload.TseDate = String(payload.TseDate).split("T")[0];
        }

        await TrainingSessionService.save(payload, reqId);

        // Actualizar referencia local para reflejar cambios inmediatos
        this.session.TseMaterialLink = payload.TseMaterialLink;
        this.session.TseMaterialPath = payload.TseMaterialPath;

        this.$fun.alert("Material de presentación guardado correctamente.", "success");
        this.$emit("saved");
        this.internalValue = false;
      } catch (err) {
        this.$fun.alert(err?.response?.data?.Message || "Error al guardar el material de la sesión.", "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0;
}
.border-bottom {
  border-bottom: 1px solid #e2e8f0;
}
.bg-gray-50 {
  background-color: #f8fafc;
}
</style>
